import React, { useState, useEffect, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UserList from "../components/UserList.jsx";
import AdminUserList from "../components/AdminUserList.jsx";
import CompanyList from "../components/CompanyList.jsx";
import QuestionList from "../components/QuestionList";
import { fetchLoginUser } from "../services/callApi";
import { Helmet } from "react-helmet";

const MANAGER_TABS = [
  {
    key: "userList",
    label: "ユーザー一覧",
    allowedRoles: ["super_admin", "admin"],
    Component: UserList,
  },
  {
    key: "adminUserList",
    label: "管理者一覧",
    allowedRoles: ["super_admin"],
    Component: AdminUserList,
  },
  {
    key: "companyList",
    label: "会社一覧",
    allowedRoles: ["super_admin", "admin"],
    Component: CompanyList,
  },
  {
    key: "questionList",
    label: "問題一覧",
    allowedRoles: ["super_admin"],
    Component: QuestionList,
  },
];

function Manager() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabKey, setTabKey] = useState(0);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [restrictedTabKeys, setRestrictedTabKeys] = useState([]);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const role = adminUser?.role ?? "";
  const visibleTabs = MANAGER_TABS.filter((tab) =>
    tab.allowedRoles.includes(role) && !restrictedTabKeys.includes(tab.key)
  );
  const activeTabIndex = selectedTab < visibleTabs.length ? selectedTab : 0;
  const selectedVisibleTab = visibleTabs[activeTabIndex];
  const SelectedTabComponent = selectedVisibleTab?.Component;

  useEffect(() => {
    let isMounted = true;

    const fetchLoginUserInfo = async () => {
      try {
        const loginUser = await fetchLoginUser();
        if (!isMounted) {
          return;
        }
        setAdminUser(loginUser);
        setErrorMessage("");
      } catch (err) {
        const status = err.status ?? err.response?.status;
        if (status === 401) {
          window.location.href = "/login";
          return;
        }
        if (isMounted) {
          setErrorMessage(
            err.message ||
              "ログインユーザー情報の取得に失敗しました。時間を置いて再試行してください。"
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLoginUserInfo();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!loading && visibleTabs.length > 0 && selectedTab >= visibleTabs.length) {
      setSelectedTab(0);
    }
  }, [loading, selectedTab, visibleTabs.length]);

  const handleForbidden = useCallback((tabKey) => {
    setRestrictedTabKeys((current) =>
      current.includes(tabKey) ? current : [...current, tabKey]
    );
    setSelectedTab(0);
    setTabKey((current) => current + 1);
  }, []);

  // タブの変更時に選択されたタブを更新し、キーを変えて再レンダリングをトリガー
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setTabKey((prevKey) => prevKey + 1); // キーを更新して再レンダリングを強制
  };

  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'flex' },
        minHeight: { md: "calc(100vh - 136px)" },
      }}
    >
      <Helmet>
        <title>適性テスト 管理者画面</title>
      </Helmet>
      {loading ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : errorMessage ? (
        <Box
          sx={{
            flexGrow: 1,
            bgcolor: "background.paper",
            p: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          <Typography color="error" sx={{ whiteSpace: "pre-line" }}>
            {errorMessage}
          </Typography>
        </Box>
      ) : visibleTabs.length === 0 ? (
        <Box
          sx={{
            flexGrow: 1,
            bgcolor: "background.paper",
            p: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          <Typography color="error" sx={{ whiteSpace: "pre-line" }}>
            この管理画面を表示する権限がありません。管理者に問い合わせてください。
          </Typography>
        </Box>
      ) : (
        <>
      {/* タブのリスト */}
      <Tabs
        orientation={isMdUp ? "vertical" : "horizontal"}
        variant={isMdUp ? "standard" : "scrollable"}
        allowScrollButtonsMobile={!isMdUp}
        value={activeTabIndex}
        onChange={handleTabChange}
        aria-label="manager tabs"
        sx={{
          width: { xs: '100%', md: 200 },
          // 一覧テーブルの最小幅に引っ張られて左ナビが消えないよう固定する
          minWidth: { md: 200 },
          flexShrink: 0,
          borderRight: { xs: 0, md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
        }}
      >
        {visibleTabs.map((tab) => (
          <Tab
            key={tab.key}
            label={tab.label}
            onClick={() => setTabKey((prevKey) => prevKey + 1)}
          />
        ))}
      </Tabs>

      {/* タブの内容 */}
      <Box sx={{ flexGrow: 1, minWidth: 0, bgcolor: "background.paper", p: { xs: 2, sm: 2.5, md: 3 }, pb: { xs: 6, sm: 8 } }}>
        {SelectedTabComponent && (
          <SelectedTabComponent
            key={tabKey}
            role={role}
            onForbidden={handleForbidden}
          />
        )}
      </Box>
        </>
      )}
    </Box>
  );
}

export default Manager;
